import { gql } from "@apollo/client";
import { POByNumber } from "../../modules/entityModules/CPOImport/query";

const getPO = /* GraphQL */ `
  query GetPO($id: ID!) {
    getPO(id: $id) {
      id
      openQty
      qty
      number
      materialEntityRelation {
        id
        code
      }
      __typename
    }
  }
`;

const updatePO = /* GraphQL */ `
  mutation UpdatePO($input: UpdatePOInput!, $condition: ModelPOConditionInput) {
    updatePO(input: $input, condition: $condition) {
      id
      number
      openQty
      qty
      __typename
    }
  }
`;

/**
 * Reverses quantities for deleted shipments based on CSV data
 * @param {Object} params
 * @param {Object} params.client - Apollo Client instance
 * @param {Array} params.csvData - Array of objects with {code, poNumber, quantity}
 * @returns {Promise<Object>} Summary of operations
 */
export default async function reverseDeletedShipmentQuantities({
  client,
  csvData,
}) {
  const results = {
    processed: 0,
    reversed: 0,
    notFound: [],
    alreadyReversed: [],
    errors: [],
  };

  for (const row of csvData) {
    try {
      const { code, poNumber, quantity } = row;
      const poNumberFormatted = `A6P-${poNumber}`;
      const itemNumber = "00010";
      const qty = parseFloat(quantity);

      if (!code || !poNumber || !quantity || isNaN(qty)) {
        results.errors.push({
          row,
          error: "Invalid data: missing code, poNumber, or quantity",
        });
        continue;
      }

      // Query PO by number and itemNumber
      let nextToken = null;
      let foundPO = null;

      do {
        const { data } = await client.query({
          query: gql(POByNumber),
          variables: {
            number: poNumberFormatted,
            itemNumber: { eq: itemNumber },
            limit: 1000,
            nextToken,
          },
          fetchPolicy: "network-only",
        });

        const items = data?.POByNumber?.items || [];
        
        // Find PO with matching code
        foundPO = items.find(
          (po) => po?.materialEntityRelation?.code === code
        );

        if (foundPO) {
          break;
        }

        nextToken = data?.POByNumber?.nextToken;
      } while (nextToken && !foundPO);

      if (!foundPO) {
        results.notFound.push({
          code,
          poNumber: `${poNumberFormatted}-${itemNumber}`,
          quantity: qty,
        });
        continue;
      }

      // Get current PO state
      const { data: poData } = await client.query({
        query: gql(getPO),
        variables: { id: foundPO.id },
        fetchPolicy: "network-only",
      });

      const currentPO = poData?.getPO;
      if (!currentPO) {
        results.errors.push({
          row,
          error: "Could not fetch PO details",
        });
        continue;
      }

      const currentOpenQty = parseFloat(currentPO.openQty) || 0;
      const originalQty = parseFloat(currentPO.qty) || 0;
      const newOpenQty = currentOpenQty + qty;

      // Check if quantity has already been reversed manually
      // If openQty is already at or above what it would be after reversal, it was already reversed
      // We check if currentOpenQty + qty would exceed originalQty, or if openQty is already >= originalQty
      // We allow a small tolerance (0.01) for floating point precision issues
      if (currentOpenQty >= originalQty - 0.01 || newOpenQty > originalQty + 0.01) {
        results.alreadyReversed.push({
          code,
          poNumber: `${poNumberFormatted}-${itemNumber}`,
          quantity: qty,
          currentOpenQty,
          originalQty,
          wouldBeOpenQty: newOpenQty,
        });
        continue;
      }

      // Reverse the quantity by adding it back to openQty
      await client.mutate({
        mutation: gql(updatePO),
        variables: {
          input: {
            id: foundPO.id,
            openQty: newOpenQty,
          },
        },
      });

      results.reversed++;
      results.processed++;

      console.log(
        `Reversed ${qty} for PO ${poNumberFormatted}-${itemNumber} (code: ${code}). New openQty: ${newOpenQty}`
      );
    } catch (error) {
      results.errors.push({
        row,
        error: error.message,
      });
      console.error(`Error processing row:`, row, error);
    }
  }

  return results;
}
